import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { newsAPI } from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import { formatNewsDate } from '../../utils/dateUtils';
import { ensureHttps } from '../../utils/urlUtils';
import './HomePage.scss';

const HomePage = () => {
  const { t, getLocalizedField, language } = useSettings();
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    fetchNews();
  }, [selectedCategory]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      
      // Параллельная загрузка новостей и категорий
      const [newsResponse, categoriesResponse] = await Promise.all([
        newsAPI.getAll(selectedCategory ? { category__slug: selectedCategory } : {}),
        categories.length === 0 ? newsAPI.getCategories() : Promise.resolve({ data: categories })
      ]);

      const newsData = newsResponse.data?.results || newsResponse.data || [];
      setNews(Array.isArray(newsData) ? newsData : []);

      if (categories.length === 0) {
        const categoriesData = categoriesResponse.data?.results || categoriesResponse.data || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      }
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredNews = Array.isArray(news) ? news.filter(item =>
    item.title?.toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  return (
    <div className="home-page">
      {/* Блок библиотеки */}
      <section className="library-hero">
        <div className="container">
          <div className="library-content">
            <div className="library-text">
              <h1 className="library-title">📚 {t('libraryHeroTitle')}</h1>
              <p className="library-description">
                {t('libraryHeroDesc')}
              </p>
            </div>
            <Link to="/library" className="library-button">
              {t('goToLibrary')}
            </Link>
          </div>
        </div>
      </section>

      {/* Поиск новостей */}
      <section className="news-search-section">
        <div className="container">
          <h2 className="section-title">{t('newsTitle')}</h2>
          <div className="search-controls">
            <div className="search-box">
              <input
                type="text"
                placeholder={t('newsSearchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <button className="search-button">🔍</button>
            </div>
            
            <div className="view-toggle">
              <button 
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Сетка"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 3H3V10H10V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 3H14V10H21V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 14H14V21H21V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 14H3V21H10V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="Список"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 6H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 12H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 18H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="category-filter">
            <button
              className={`category-chip ${!selectedCategory ? 'active' : ''}`}
              onClick={() => setSelectedCategory(null)}
            >
              {t('categoryAll')}
            </button>
            {Array.isArray(categories) && categories.map((category) => (
              <button
                key={category.id}
                className={`category-chip ${selectedCategory === category.slug ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.slug)}
              >
                {getLocalizedField(category, 'name')}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Список всех новостей */}
      <section className="news-list-section">
        <div className="container">
          {loading ? (
            <div className="loading">{t('newsLoading')}</div>
          ) : filteredNews.length === 0 ? (
            <div className="no-results">{t('newsNotFound')}</div>
          ) : (
            <div className={`news-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
              {filteredNews.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.slug}`}
                  className="news-card"
                >
                  {item.cover_image && (
                    <div className="news-image">
                      <img src={ensureHttps(item.cover_image)} alt={getLocalizedField(item, 'title')} />
                      {item.is_breaking && (
                        <span className="breaking-badge">{t('breakingNews')}</span>
                      )}
                      {item.is_featured && (
                        <span className="featured-badge">{t('featuredNews')}</span>
                      )}
                    </div>
                  )}
                  <div className="news-content">
                    {item.category && (
                      <span className="news-category">{getLocalizedField(item.category, 'name')}</span>
                    )}
                    <h3 className="news-title">{getLocalizedField(item, 'title')}</h3>
                    <p className="news-summary">{getLocalizedField(item, 'summary')}</p>
                    <div className="news-meta">
                      <span className="news-date">
                        {formatNewsDate(item.published_at || item.created_at, language, t)}
                      </span>
                      <span className="news-views">{t('viewsCount')} {item.views_count}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
